import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Stream from 'flarum/common/utils/Stream';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';

const endpoint = () => `${app.forum.attribute('apiUrl')}/ghost-block/separations`;

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_LENGTH = 2;
const SEARCH_LIMIT = 6;

/**
 * Moderator tool: separate the given user from another user (mutual ghost
 * mode they can't lift themselves), and list / lift existing separations.
 */
export default class SeparateUsersModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.otherUsername = Stream('');
    this.separations = null;
    this.removingId = null;
    this.searchResults = [];
    this.searching = false;
    this.searchTimeout = null;
    this.searchQuery = '';

    this.load();
  }

  onremove(vnode) {
    super.onremove(vnode);
    clearTimeout(this.searchTimeout);
  }

  className() {
    return 'GhostBlock-separateModal Modal--small';
  }

  title() {
    return app.translator.trans('happydigital-ghost-block.forum.separate.title', { username: this.attrs.user.username() });
  }

  content() {
    return (
      <div className="Modal-body">
        <p className="helpText">{app.translator.trans('happydigital-ghost-block.forum.separate.help')}</p>

        <div className="Form-group GhostBlock-search">
          <input
            className="FormControl"
            name="otherUsername"
            autocomplete="off"
            placeholder={app.translator.trans('happydigital-ghost-block.forum.separate.username_placeholder')}
            value={this.otherUsername()}
            oninput={(e) => this.onSearchInput(e.target.value)}
            disabled={this.loading}
          />
          {this.searchResultsView()}
        </div>

        <div className="Form-group">
          <Button type="submit" className="Button Button--primary Button--block" loading={this.loading} disabled={!this.otherUsername().trim()}>
            {app.translator.trans('happydigital-ghost-block.forum.separate.submit_button')}
          </Button>
        </div>

        {this.listView()}
      </div>
    );
  }

  searchResultsView() {
    if (this.searching && !this.searchResults.length) {
      return (
        <div className="GhostBlock-searchResults">
          <LoadingIndicator size="small" display="block" />
        </div>
      );
    }

    if (!this.searchResults.length) return null;

    return (
      <ul className="GhostBlock-searchResults">
        {this.searchResults.map((user) => (
          <li key={user.id()}>
            <button type="button" className="GhostBlock-searchResult" onclick={() => this.selectUser(user)}>
              {avatar(user)} {username(user)}
            </button>
          </li>
        ))}
      </ul>
    );
  }

  onSearchInput(value) {
    this.otherUsername(value);
    clearTimeout(this.searchTimeout);

    const query = value.trim();

    if (query.length < SEARCH_MIN_LENGTH) {
      this.searchQuery = '';
      this.searchResults = [];
      this.searching = false;
      return;
    }

    this.searching = true;
    this.searchTimeout = setTimeout(() => this.search(query), SEARCH_DEBOUNCE_MS);
  }

  search(query) {
    this.searchQuery = query;

    // Core user search; needs the "view user list" permission, which
    // moderators normally have. Without it the field still works as plain input.
    app.store
      .find('users', { filter: { q: query }, page: { limit: SEARCH_LIMIT } })
      .then((users) => {
        // Ignore responses for queries the moderator has already typed past.
        if (this.searchQuery !== query) return;

        const subjectId = this.attrs.user.id();
        this.searchResults = users.filter((user) => user.id() !== subjectId);
      })
      .catch(() => {
        this.searchResults = [];
      })
      .then(() => {
        if (this.searchQuery === query) this.searching = false;
        m.redraw();
      });
  }

  selectUser(user) {
    this.setUsername(user.username());
  }

  /** Sets the field and closes any open search results. */
  setUsername(value) {
    clearTimeout(this.searchTimeout);
    this.otherUsername(value);
    this.searchQuery = '';
    this.searchResults = [];
    this.searching = false;
  }

  listView() {
    if (this.separations === null) return <LoadingIndicator />;

    if (!this.separations.length) {
      return <p className="GhostBlock-note">{app.translator.trans('happydigital-ghost-block.forum.separate.empty')}</p>;
    }

    return (
      <ul className="GhostBlock-listItems">
        {this.separations.map((separation) => (
          <li className="GhostBlock-listItem" key={separation.id}>
            <span className="GhostBlock-listUser">{separation.username}</span>
            <Button
              className="Button GhostBlock-listUnblock"
              icon="fas fa-times"
              loading={this.removingId === separation.id}
              onclick={() => this.remove(separation)}
            >
              {app.translator.trans('happydigital-ghost-block.forum.separate.remove_button')}
            </Button>
          </li>
        ))}
      </ul>
    );
  }

  load() {
    return app
      .request({ method: 'GET', url: endpoint(), params: { userId: this.attrs.user.id() } })
      .then((response) => {
        this.separations = response.data;
      })
      .catch(() => {
        this.separations = [];
      })
      .then(() => m.redraw());
  }

  onsubmit(e) {
    e.preventDefault();
    this.loading = true;

    app
      .request({
        method: 'POST',
        url: endpoint(),
        body: { userId: this.attrs.user.id(), otherUsername: this.otherUsername().trim() },
        errorHandler: this.onerror.bind(this),
      })
      .then(() => {
        this.setUsername('');
        return this.load();
      })
      .catch(() => {})
      .then(() => {
        this.loading = false;
        m.redraw();
      });
  }

  remove(separation) {
    this.removingId = separation.id;

    app
      .request({ method: 'DELETE', url: `${endpoint()}/${separation.id}` })
      .then(() => {
        this.separations = this.separations.filter((s) => s.id !== separation.id);
      })
      .catch(() => {})
      .then(() => {
        this.removingId = null;
        m.redraw();
      });
  }
}
